/**
 * ReportModal.js
 * Modal for users to report bad actors.
 */
import { getFirestore, collection, addDoc, serverTimestamp } from "firebase/firestore";
import { app } from "../../firebase-config.js";
import { locale } from "../../services/LocaleService.js";

const db = getFirestore(app);

export const ReportModal = {
    async show(targetUserId, contentId, contentType, source = 'general') {
        if (!window.user) {
            window.Modal.alert(locale.t('report.login_required'));
            return;
        }

        // Create Modal UI
        // 1. Overlay (Background) - Z-Index 9990 (Below Header)
        const overlay = document.createElement('div');
        overlay.className = "fixed inset-0 bg-black/60 z-[9990] transition-opacity duration-300 opacity-0 backdrop-blur-sm";

        // 2. Wrapper (Positioning) - Z-Index 10001 (Above Header)
        const wrapper = document.createElement('div');
        wrapper.className = "fixed inset-0 z-[10001] flex items-center justify-center pointer-events-none p-4";

        // 3. Modal Content (Interactive)
        const modal = document.createElement('div');
        modal.className = "bg-white dark:bg-zinc-900 rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden scale-95 opacity-0 transition-all duration-300 relative pointer-events-auto";
        modal.id = "report-modal-content";
        modal.innerHTML = `
            <div class="p-6">
                <h3 class="text-xl font-bold text-red-600 mb-2 flex items-center gap-2">
                    <span class="material-symbols-outlined">report</span>
                    ${locale.t('report.modal.title')}
                </h3>
                <p class="text-gray-600 dark:text-gray-400 text-sm mb-4">
                    ${locale.t('report.modal.desc')}
                </p>

                <form id="report-form" class="space-y-4">
                    <!-- Reason Selection -->
                    <div class="space-y-2">
                        <label class="block text-xs font-bold text-gray-500 uppercase">${locale.t('report.modal.category')}</label>
                        <div class="grid grid-cols-2 gap-2">
                            <label class="flex items-center gap-2 p-2 rounded border border-gray-200 dark:border-zinc-700 hover:bg-gray-50 dark:hover:bg-zinc-800 cursor-pointer">
                                <input type="radio" name="reason" value="spam" class="text-red-600 focus:ring-red-500" checked>
                                <span class="text-sm text-gray-900 dark:text-gray-100">${locale.t('report.reason.spam')}</span>
                            </label>
                            <label class="flex items-center gap-2 p-2 rounded border border-gray-200 dark:border-zinc-700 hover:bg-gray-50 dark:hover:bg-zinc-800 cursor-pointer">
                                <input type="radio" name="reason" value="abusive" class="text-red-600 focus:ring-red-500">
                                <span class="text-sm text-gray-900 dark:text-gray-100">${locale.t('report.reason.abusive')}</span>
                            </label>
                            <label class="flex items-center gap-2 p-2 rounded border border-gray-200 dark:border-zinc-700 hover:bg-gray-50 dark:hover:bg-zinc-800 cursor-pointer">
                                <input type="radio" name="reason" value="inappropriate" class="text-red-600 focus:ring-red-500">
                                <span class="text-sm text-gray-900 dark:text-gray-100">${locale.t('report.reason.inappropriate')}</span>
                            </label>
                            <label class="flex items-center gap-2 p-2 rounded border border-gray-200 dark:border-zinc-700 hover:bg-gray-50 dark:hover:bg-zinc-800 cursor-pointer">
                                <input type="radio" name="reason" value="other" class="text-red-600 focus:ring-red-500">
                                <span class="text-sm text-gray-900 dark:text-gray-100">${locale.t('report.reason.other')}</span>
                            </label>
                        </div>
                    </div>

                    <!-- [ADDED] Description Field -->
                    <div class="space-y-1">
                        <label class="block text-xs font-bold text-gray-500 uppercase">${locale.t('report.modal.detail')}</label>
                        <textarea id="report-description" rows="3" class="w-full px-3 py-2 border border-gray-300 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm resize-none" placeholder="${locale.t('report.placeholder_content')}"></textarea>
                    </div>

                    <!-- [ADDED] URL Field -->
                    <div class="space-y-1">
                        <label class="block text-xs font-bold text-gray-500 uppercase">${locale.t('report.modal.url')}</label>
                        <input type="text" id="report-url" class="w-full px-3 py-2 border border-gray-300 dark:border-zinc-700 bg-gray-100 dark:bg-zinc-800 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm" placeholder="https://..." value="${window.location.href}" readonly>
                    </div>
                </form>
            </div>
            <div class="p-4 bg-gray-50 dark:bg-zinc-950 flex justify-end gap-3">
                <button id="btn-cancel-report" class="mapbox-btn px-4 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-zinc-800 dark:hover:bg-zinc-700 text-gray-800 dark:text-gray-200 text-sm font-medium rounded-lg">${locale.t('report.modal.cancel')}</button>
                <button id="btn-submit-report" class="mapbox-btn px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg flex items-center gap-1">
                    <span class="material-symbols-outlined text-sm">send</span> ${locale.t('report.modal.submit')}
                </button>
            </div>
        `;

        wrapper.appendChild(modal);
        document.body.appendChild(overlay);
        document.body.appendChild(wrapper);

        // Animation
        requestAnimationFrame(() => {
            overlay.classList.remove('opacity-0');
            modal.classList.remove('scale-95', 'opacity-0');
        });

        const close = () => {
            overlay.classList.add('opacity-0');
            modal.classList.add('scale-95', 'opacity-0');
            setTimeout(() => {
                if (overlay.isConnected) overlay.remove();
                if (wrapper.isConnected) wrapper.remove();
            }, 300);
        };

        overlay.onclick = () => close();
        modal.querySelector('#btn-cancel-report').onclick = close;

        modal.querySelector('#btn-submit-report').onclick = async () => {
            const reason = modal.querySelector('input[name="reason"]:checked').value;
            const description = modal.querySelector('#report-description').value.trim();
            const url = modal.querySelector('#report-url').value.trim();

            try {
                // [FIX v0.804] Paranoid Sanitization for Robustness
                const safeReporterId = window.user?.uid || 'anonymous';
                const safeTargetId = targetUserId || null;
                const safeContentId = contentId || null;
                const safeContentType = contentType || 'comment';
                const safeSource = source || 'general';
                const safeReason = reason || 'other';

                // Unified Payload
                const reportData = {
                    reporterId: safeReporterId,
                    targetUserId: safeTargetId,
                    contentId: safeContentId,
                    contentType: safeContentType,
                    reason: safeReason,
                    source: safeSource,

                    // [ADDED] General Report Fields
                    description: description, // Matches 'content' conceptually
                    content: description,     // Legacy field compatibility
                    url: url,                 // Legacy field compatibility

                    createdAt: new Date(), // Client timestamp
                    isResolved: false
                };

                console.log("[ReportModal] Payload:", reportData);

                await addDoc(collection(db, "reports"), reportData);

                close();
                // [FIX] Native Alert fallback to ensure user sees success
                // [ADDED] Notification
                import("../../services/notification-trigger.service.js").then(({ NotificationTriggerService }) => {
                    NotificationTriggerService.notifyReceipt(safeReporterId, '신고');
                });

                setTimeout(() => alert(locale.t('report.submit_success') + "\n" + locale.t('report.success_desc')), 200);

            } catch (error) {
                console.error("Report Error:", error);
                alert(locale.t('report.error') + ": " + error.message);
            }
        };
    }
};
